import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { useProjectsStore } from "@/stores/projects";
import { useShallow } from 'zustand/react/shallow';

export const ProjectSwitcher = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const {
    byId: projectsById,
    list: projectIds,
    selectProject,
    activeProjectId,
  } = useProjectsStore(useShallow(state => ({
    byId: state.byId,
    list: state.list,
    selectProject: state.selectProject,
    activeProjectId: state.activeProjectId,
  })));

  const activeProject = useMemo(
    () => (activeProjectId ? projectsById[activeProjectId] : null),
    [activeProjectId, projectsById]
  );

  const allProjects = projectIds.map((id) => projectsById[id]).filter(Boolean);
  const handleProjectChange = async (projectId: string) => {
    if (projectId === activeProjectId) return;
    try {
      await selectProject(projectId);
    } catch (e: any) {
      toast.error("Failed to switch project", { description: e.message });
    }
  };

  if (!activeProject) {
    return null;
  }

  return isMobile ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <span>{activeProject.name}</span>
          <span className="text-xs">▼</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>Switch Project</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {allProjects.map((project) => (
          <DropdownMenuItem
            key={project.id}
            disabled={project.id === activeProjectId}
            onSelect={() => handleProjectChange(project.id)}
          >
            {project.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : (
    <div className="flex items-center gap-1">
      {allProjects.map((project) => (
        <Button
          key={project.id}
          variant={project.id === activeProjectId ? "secondary" : "ghost"}
          size="sm"
          className="h-8"
          onClick={() => handleProjectChange(project.id)}
        >
          {project.name}
        </Button>
      ))}
      <Button
        variant="ghost"
        size="sm"
        className="h-8"
        onClick={() => navigate("/pages/projects/")}
      >
        + Manage
      </Button>
    </div>
  );
};